import axios from "axios";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TablePagination from "../components/shared/TablePagination";
import { API_BASE_URL } from "../config";
import { useAuth } from "../components/shared/AuthContext";
import Swal from "sweetalert2";
import { FaSearch } from "react-icons/fa";
import { Button as ButtonUI, PageLayout, SearchInput, PlusIcon, LogoLoader } from "../components/ui";

const EntityDetails = (props) => {
  const { permissions, data, roleName, jwt } = useAuth(); // Get jwt for auth headers
  const location = useLocation();
  const navigate = useNavigate();
  
  const [tableData, setTableData] = useState([]); // Renamed data to tableData to avoid conflict
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showDelModal, setShowDelModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [personalDetails, setPersonalDetails] = useState([]);
  const [transactionsData, setTransactionsData] = useState([]);
  const [isHovered, setIsHovered] = useState(false);

  const entity = props.entity || location.state?.entity;
  const userBranchId = data?.branchId; // Get branchId from data instead of userdata
  
  // Determine if user can view all branches or just their own
  const isSuperAdmin = roleName === "superadmin";
  const isAdmin = roleName === "admin";
  const isDataEntryOperator = roleName === "Data Entry Operator";
  
  // Only apply branch filtering for admin and data entry operator, not for superadmin
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  const hasAddPermission = permissions.find(
    (permission) => permission.page === entity && permission.add
  );
  const hasEditPermission = permissions.find(
    (permission) => permission.page === entity && permission.edit
  );
  const hasDelPermission = permissions.find(
    (permission) => permission.page === entity && permission.del
  );

  const entityMap = useMemo(
    () => {
      // Apply branch filtering only for non-superadmin users
      const branchFilter = shouldFilterByBranch ? `&filter={"branchId":${userBranchId}}` : '';
      // For transactions we need to filter by branchId directly, not through relations
      const customerBranchFilter = shouldFilterByBranch ? `&filter={"branchId":${userBranchId}}` : '';
      
      return {
        branches: {
          title: "Branches",
          singular: "Branch",
          navigationPath: "addEditBranch",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/branch_details?fields=id,branchName,branchUniqueId${shouldFilterByBranch ? `&filter={"id":${userBranchId}}` : ''}`,
          model: "branch_details",
        },
        centers: {
          title: "Centers",
          singular: "Center",
          navigationPath: "addEditCenter",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/center_details?fields=id,centerName,centerUniqueId${branchFilter}`,
          model: "center_details",
        },
        roles: {
          title: "Roles",
          singular: "Role",
          navigationPath: "addEditRole",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/roles?fields=id,roleName&notInclude={"roleName":["superadmin"]}`,
          model: "roles",
        },
        staff: {
          title: "Staff",
          singular: "Staff",
          navigationPath: "addEditStaff",
          // childToParent, NOT a bare includeDepth=1. The plain form also pulls
          // the hasMany side — every transaction for every staff member came
          // back in testing. Parent-only returns just the role and branch, which
          // is all the BRANCH column needs.
          apiEndpoint: `${API_BASE_URL}/api/getModelData/staff_details?fields=id,staffId,name,designation,branchId&includeDepth=1&relationshipType=childToParent&notInclude={"staffId":[1]}${branchFilter}`,
          model: "staff_details",
        },
        accounts: {
          title: "Accounts",
          singular: "Account",
          navigationPath: "addEditAccount",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/transactions?fields=id,customerUniqueId,transactionId,transactionType,accountType,cash_type,amount,date&includeDepth=1${customerBranchFilter}`,
          model: "transactions",
        },
        deposits: {
          title: "Deposits",
          singular: "Deposit",
          navigationPath: "addEditDeposit",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/transactions?fields=id,customerUniqueId,transactionId,accountNumber,date,amount&filter={"transactionType":"deposit"}&includeDepth=1${customerBranchFilter}`,
          model: "transactions",
        },
        withdrawals: {
          title: "Withdrawals",
          singular: "Withdrawal",
          navigationPath: "addEditWithdraw",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/transactions?fields=id,customerUniqueId,transactionId,accountNumber,date,amount&filter={"transactionType":"withdraw"}&includeDepth=1${customerBranchFilter}`,
          model: "transactions",
        },
        funding_agencies: {
          title: "Funding Agencies",
          singular: "Funding Agency",
          navigationPath: "addEditFundingAgency",
          apiEndpoint: `${API_BASE_URL}/api/getModelData/funding_agencies`,
          model: "funding_agencies",
        },
      };
    },
    [userBranchId, shouldFilterByBranch]
  );

  useEffect(() => {
    if (entityMap[entity]) {
      setSelectedEntity(entityMap[entity]);
      fetchData(entityMap[entity].apiEndpoint);
    } else {
      console.error("Invalid entity parameter");
    }
    getStaffDetails();
  }, [entity, entityMap, userBranchId, shouldFilterByBranch]);

  const getStaffDetails = async () => {
    try {
      // Must match the apiEndpoint above — both build the staff request, and a
      // difference here means the branch name is present on one path and not the
      // other.
      let apiUrl = `${API_BASE_URL}/api/getModelData/staff_details?fields=id,staffId,name,designation,branchId&includeDepth=1&relationshipType=childToParent&notInclude={"staffId":[1]}`;
      
      // Add branchId filter for non-superadmin users
      if (shouldFilterByBranch) {
        apiUrl += `&filter={"branchId":${userBranchId}}`;
      }

      const response = await axios.get(apiUrl);
      const staffData = response.data.data || [];
      
      setStaff(staffData);
    } catch (error) {
      console.error("Error fetching staff details:", error);
      setStaff([]);
    }
  };

  const fetchData = async (apiEndpoint) => {
    try {
      console.log("Fetching data with endpoint:", apiEndpoint);
      const response = await axios.get(apiEndpoint);
      let fetchedData = response.data.data || []; // Changed variable name to avoid conflict

      console.log("Raw data for entity:", entity, response.data.data);

      if (entity === "accounts") {
        fetchedData = fetchedData.map((transaction) => ({
          id: transaction.id || null,
          customerUniqueId: transaction.customerUniqueId || "N/A",
          transactionId: transaction.transactionId || "N/A",
          accountNumber: transaction.accountNumber || "N/A",
          transactionType: transaction.transactionType || "N/A",
          accountType: transaction.accountType || "N/A",
          cash_type: transaction.cash_type || "N/A",
          amount: transaction.amount != null ? transaction.amount : 0,
          date: transaction.date
            ? new Date(transaction.date).toISOString().split("T")[0]
            : "N/A",
        }));

        console.log("Normalized transactions data:", fetchedData);

        // Date filtering bypassed as requested to show all transaction data by default

        console.log("Filtered transactions data:", fetchedData);

        // Get personal details with branch filtering if applicable
        let personalDetailsUrl = `${API_BASE_URL}/api/getModelData/personal_details?fields=id,name,customerUniqueId,branchId`;
        if (shouldFilterByBranch) {
          personalDetailsUrl += `&filter={"branchId":${userBranchId}}`;
        }
        console.log("Personal details URL:", personalDetailsUrl);
        const personalDetailsResponse = await axios.get(personalDetailsUrl);
        const personalDetailsData = personalDetailsResponse.data.data || [];
        setPersonalDetails(personalDetailsData);

        const personalDetailsMap = new Map(
          personalDetailsData.map((person) => [
            person.customerUniqueId,
            person.name,
          ])
        );

        fetchedData = fetchedData.map((transaction) => ({
          ...transaction,
          name: personalDetailsMap.get(transaction.customerUniqueId) || "N/A",
        }));
        setTransactionsData(fetchedData);
      } else if (entity === "deposits" || entity === "withdrawals") {
        // Get personal details with branch filtering if applicable
        let personalDetailsUrl = `${API_BASE_URL}/api/getModelData/personal_details?fields=id,name,customerUniqueId,branchId`;
        if (shouldFilterByBranch) {
          personalDetailsUrl += `&filter={"branchId":${userBranchId}}`;
        }
        const personalDetailsResponse = await axios.get(personalDetailsUrl);
        const personalDetailsData = personalDetailsResponse.data.data || [];
        setPersonalDetails(personalDetailsData);

        const personalDetailsMap = new Map(
          personalDetailsData.map((person) => [
            person.customerUniqueId,
            person.name,
          ])
        );

        fetchedData = fetchedData.map((transaction) => ({
          name: personalDetailsMap.get(transaction.customerUniqueId) || "N/A",
          ...transaction,
          amount: transaction.amount != null ? transaction.amount : 0,
          date: transaction.date
            ? new Date(transaction.date).toISOString().split("T")[0]
            : "N/A",
        }));
      } else if (entity === "roles") {
        fetchedData = fetchedData.map((role) => ({
          id: role.id || null,
          roleName: role.roleName || "N/A",
        }));
      } else if (entity === "staff") {
        fetchedData = fetchedData.map((staff) => ({
          id: staff.id || null,
          staffId: staff.staffId || "N/A",
          name: staff.name || "N/A",
          designation: staff.designation || "N/A",
          // Kept: the branch-scope filter immediately below compares on it, so
          // dropping it would silently stop branch filtering for non-superadmins.
          branchId: staff.branchId || "N/A",
          // "N/A" rather than blank — staff id 1 carries branchId null.
          branchName:
            staff.fk_staff_details_belongsTo_branch_details_branchId?.branchName ||
            "N/A",
        }));
        
        // API-based filtering is already applied, but double-check for any missing filters
        if (shouldFilterByBranch && fetchedData.some(staff => staff.branchId !== userBranchId)) {
          fetchedData = fetchedData.filter(staff => staff.branchId === userBranchId);
        }
      }

      setTableData(fetchedData); // Changed from setData to setTableData
      setFilteredData(fetchedData);
    } catch (error) {
      console.error(`Error fetching data for ${entity}:`, error);
      setTableData([]); // Changed from setData to setTableData
      setFilteredData([]);
    }
  };

  useEffect(() => {
    if (selectedEntity && entity === "accounts") {
      fetchData(selectedEntity.apiEndpoint);
    }
  }, [selectedEntity, entity]);

  const handleSearch = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredData(tableData); // Changed from data to tableData
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const filtered = tableData.filter((item) => // Changed from data to tableData
      Object.keys(item).some(
          (key) =>
            item[key] && item[key].toString().toLowerCase().includes(lowerTerm)
        )
      );
      setFilteredData(filtered);
    }, [searchTerm, tableData]); // Changed from data to tableData
  
    useEffect(() => {
      handleSearch();
    }, [searchTerm, handleSearch]);
  
    const handleAddClick = () => {
      if (selectedEntity) {
        navigate(`/${selectedEntity.navigationPath}`, {
          state: { type: "Add", entity },
        });
      }
    };
  
    const handleViewClick = (id) => {
      if (selectedEntity) {
        navigate(`/${selectedEntity.navigationPath}`, {
          state: { type: "View", id, entity },
        });
      }
    };
  
    const handleEditClick = (id) => {
      if (selectedEntity) {
        navigate(`/${selectedEntity.navigationPath}`, {
          state: { type: "Edit", id, entity },
        });
      }
    };
  
    const handleDelClick = useCallback((id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EF4444",
        cancelButtonColor: "#64748B",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        customClass: {
          popup: 'rounded-xl font-sans',
          confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold',
          cancelButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
        }
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const res = await axios.post(`${API_BASE_URL}/api/delDetails`, {
              model: selectedEntity?.model,
              id: id,
            }, {
              headers: {
                "x-token": jwt,
              },
            });
            if (res.data.message) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Data has been deleted successfully.",
                confirmButtonColor: "#004BB5",
                customClass: {
                  popup: 'rounded-xl font-sans',
                  confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
                }
              });
              fetchData(selectedEntity.apiEndpoint);
            } else if (res.data.error) {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: res.data.error,
                confirmButtonColor: "#004BB5",
                customClass: {
                  popup: 'rounded-xl font-sans',
                  confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
                }
              });
            }
          } catch (err) {
            const errorMsg = err.response?.data?.error || err.message || "An error occurred.";
            Swal.fire({
              icon: "error",
              title: "Error",
              text: errorMsg,
              confirmButtonColor: "#004BB5",
              customClass: {
                popup: 'rounded-xl font-sans',
                confirmButton: 'rounded-lg px-4 py-2 text-sm font-semibold'
              }
            });
          }
        }
      });
    }, [selectedEntity, jwt]);
  
    if (!selectedEntity) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <LogoLoader size="lg" />
        </div>
      );
    }

  const isTargetMaster = ["branches", "centers", "staff"].includes(entity?.toLowerCase());
  const isAccounts = entity?.toLowerCase() === "accounts";
  const hideSubtitle = [
    "branches",
    "centers",
    "staff",
    "accounts",
    "roles",
    "funding_agencies",
    "funding-agencies"
  ].includes(entity?.toLowerCase());

  const pageTitle = isAccounts
    ? undefined
    : isTargetMaster
    ? selectedEntity.singular || selectedEntity.title
    : selectedEntity.title;

  const pageSubtitle = hideSubtitle
    ? undefined
    : `Manage and view your branch ${selectedEntity.title.toLowerCase()}`;

  return (
    <PageLayout
      title={pageTitle}
      subtitle={pageSubtitle}
      actions={
        !isAccounts &&
        hasAddPermission && (
          <ButtonUI
            onClick={handleAddClick}
            leftIcon={<PlusIcon className="w-4 h-4" />}
          >
            Add {selectedEntity.singular || selectedEntity.title}
          </ButtonUI>
        )
      }
      toolbar={
        isAccounts ? (
          <>
            <div className="w-48">
              <SearchInput
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {hasAddPermission && (
              <ButtonUI
                onClick={handleAddClick}
                leftIcon={<PlusIcon className="w-4 h-4" />}
              >
                Add {selectedEntity.singular || selectedEntity.title}
              </ButtonUI>
            )}
          </>
        ) : ["deposits", "withdrawals"].includes(entity) ? (
          <div className="w-48">
            <SearchInput
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        ) : null
      }
    >
      <TablePagination
        key={entity}
        tabledata={
          ["accounts", "deposits", "withdrawals"].includes(entity)
            ? filteredData
            : tableData // Changed from data to tableData
        }
        initialRowsPerPage={15}
        showViewOption={true}
        showEditOption={
          hasEditPermission && !["deposits", "withdrawals"].includes(entity)
        }
        showDeleteOption={
          entity === "accounts"
            ? (roleName === "admin" || roleName === "superadmin")
            : (hasDelPermission && !["deposits", "withdrawals"].includes(entity))
        }
        onClickView={handleViewClick}
        onClickEdit={handleEditClick}
        onClickDel={handleDelClick}
        staffList={staff}
        entity={entity}
        personalDetails={personalDetails}
      />
    </PageLayout>
  );
};

export default EntityDetails;